import type { AttributeReviewDraftSelection } from "@/hooks/useAttributeReview.types";
import { shownAnswer } from "./shownAnswer";

// Both sides are allocated independently (live GraphQL rebuild vs localStorage
// restore), so reference identity says nothing about whether the selection
// changed; __typename rides on GraphQL objects only and is never selection data.
function comparableKeys(value: object | null | undefined): string[] {
  return value ? Object.keys(value).filter((key) => key !== "__typename") : [];
}

function valuesEqual(left: unknown, right: unknown): boolean {
  if (Object.is(left, right)) return true;
  if (
    !left ||
    !right ||
    typeof left !== "object" ||
    typeof right !== "object"
  ) {
    return false;
  }
  if (Array.isArray(left) || Array.isArray(right)) {
    return (
      Array.isArray(left) &&
      Array.isArray(right) &&
      left.length === right.length &&
      left.every((item, index) => valuesEqual(item, right[index]))
    );
  }
  return shallowObjectsEqual(left, right);
}

// Despite the historical name, this is a RECURSIVE structural compare —
// nested content equality, __typename ignored at every level.
export function shallowObjectsEqual(
  left: object | null | undefined,
  right: object | null | undefined,
): boolean {
  if (left === right) return true;
  const leftKeys = comparableKeys(left);
  const rightKeys = comparableKeys(right);
  if (leftKeys.length !== rightKeys.length) return false;

  const leftRecord = (left ?? {}) as Record<string, unknown>;
  const rightRecord = (right ?? {}) as Record<string, unknown>;
  return leftKeys.every(
    (key) =>
      Object.prototype.hasOwnProperty.call(rightRecord, key) &&
      valuesEqual(leftRecord[key], rightRecord[key]),
  );
}

export function shallowObjectArraysEqual<T extends object>(
  left: readonly T[] | null | undefined,
  right: readonly T[] | null | undefined,
): boolean {
  const leftItems = left ?? [];
  const rightItems = right ?? [];
  return (
    leftItems.length === rightItems.length &&
    leftItems.every((item, index) =>
      shallowObjectsEqual(item, rightItems[index]),
    )
  );
}

export function attributeReviewDraftSelectionsEqual(
  left: AttributeReviewDraftSelection | null | undefined,
  right: AttributeReviewDraftSelection | null | undefined,
): boolean {
  if (!left || !right) return left === right;
  return (
    left.kind === right.kind &&
    left.leaf === right.leaf &&
    left.pool === right.pool &&
    shallowObjectArraysEqual(left.values, right.values)
  );
}

type SavedRow = {
  kind?: string | null;
  leaf?: string | null;
  pool?: { key: string } | null;
  values?: { store_value: string; pool_value: string; ignored?: boolean }[];
  poolOffers?: Parameters<typeof shownAnswer>[0]["poolOffers"];
};

export function draftEqualsSavedAnswer(
  draft: AttributeReviewDraftSelection,
  row: SavedRow,
): boolean {
  const saved: AttributeReviewDraftSelection = {
    kind: draft.kind ? (row.kind ?? undefined) : undefined,
    leaf: draft.leaf ? (row.leaf ?? undefined) : undefined,
    pool: draft.pool ? row.pool?.key : undefined,
    values: draft.values?.flatMap((fix) => {
      const value = row.values?.find((v) => v.store_value === fix.storeValue);
      if (!value) return [];
      return [
        value.ignored
          ? { storeValue: fix.storeValue, ignored: true }
          : { storeValue: fix.storeValue, poolValue: value.pool_value },
      ];
    }),
  };
  return (
    attributeReviewDraftSelectionsEqual(draft, saved) &&
    shallowObjectArraysEqual(
      withFixes(shownAnswer(row, draft).values as ValueRow[], draft.values),
      (row.values ?? []).map(comparable),
    )
  );
}

type ValueRow = NonNullable<SavedRow["values"]>[number];

const comparable = (v: ValueRow) => ({
  store_value: v.store_value,
  pool_value: v.pool_value,
  ignored: Boolean(v.ignored),
});

function withFixes(
  values: ValueRow[],
  fixes: AttributeReviewDraftSelection["values"],
) {
  return values.map((v) => {
    const fix = fixes?.find((f) => f.storeValue === v.store_value);
    if (!fix) return comparable(v);
    return fix.ignored
      ? { ...comparable(v), ignored: true }
      : {
          ...comparable(v),
          pool_value: fix.poolValue ?? v.pool_value,
          ignored: false,
        };
  });
}
